// src/Studio/masks/maskRenderer.ts

import type { VectorMask, GradientFade, OpacitySettings } from '../types/gradientFade';
import { drawMaskPathToContext, offsetPath } from './maskPathUtils';
import { renderGradientToCanvas } from './gradientRenderer';

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;

const hasOffscreenCanvas = typeof OffscreenCanvas !== 'undefined';

function createCanvas(width: number, height: number): AnyCanvas {
  if (hasOffscreenCanvas) {
    return new OffscreenCanvas(width, height);
  }
  if (typeof document === 'undefined') {
    throw new Error('Canvas rendering requires a browser environment.');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

// Canvas pool for reuse
const canvasPool = {
  mask: null as AnyCanvas | null,
  gradient: null as AnyCanvas | null,
  alpha: null as AnyCanvas | null,
  output: null as AnyCanvas | null,
};

function getPooledCanvas(key: keyof typeof canvasPool, width: number, height: number): AnyCanvas {
  let canvas = canvasPool[key];
  if (!canvas || canvas.width !== width || canvas.height !== height) {
    canvas = createCanvas(width, height);
    canvasPool[key] = canvas;
  }
  return canvas;
}

// Render cache for performance
interface RenderCacheEntry {
  maskHash: string;
  gradientHash: string;
  width: number;
  height: number;
  outputBitmap: ImageBitmap | null;
}

const renderCache = new Map<string, RenderCacheEntry>();

function hashMask(mask: VectorMask | undefined): string {
  if (!mask || !mask.enabled) return 'none';
  return JSON.stringify({
    paths: mask.paths.map(p => ({ points: p.points, closed: p.closed, op: p.operation })),
    feather: mask.featherPx,
    expand: mask.expandPx,
    invert: mask.invert,
  });
}

function hashGradient(gradient: GradientFade | undefined): string {
  if (!gradient) return 'none';
  return JSON.stringify(gradient);
}

/**
 * Render the vector mask to a grayscale canvas
 * White = affected area, Black = unaffected area
 *
 * IMPORTANT: The mask defines where the opacity effect is applied.
 * Areas INSIDE the path are affected. Areas OUTSIDE stay fully visible.
 */
export function renderMaskToCanvas(
  canvas: AnyCanvas,
  mask: VectorMask,
  width: number,
  height: number
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Resize if needed
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }

  // Start with black (no effect by default)
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = 'black';
  ctx.fillRect(0, 0, width, height);

  // Process each path based on its operation
  // Paths define areas that become AFFECTED (white)
  for (const path of mask.paths) {
    if (path.points.length < 3 || !path.closed) continue;

    // Apply expand/contract
    const processedPath = mask.expandPx !== 0
      ? offsetPath(path, mask.expandPx)
      : path;

    ctx.save();

    switch (path.operation) {
      case 'replace':
        // Replace selection: clear and set new affected area
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = 'white';
        drawMaskPathToContext(ctx, processedPath);
        ctx.fill();
        break;

      case 'add':
        // Add to affected area
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'white';
        drawMaskPathToContext(ctx, processedPath);
        ctx.fill();
        break;

      case 'subtract':
        // Remove from affected area
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'black';
        drawMaskPathToContext(ctx, processedPath);
        ctx.fill();
        break;

      case 'intersect':
        // Keep only the intersection with existing affected area
        ctx.globalCompositeOperation = 'destination-in';
        ctx.fillStyle = 'white';
        drawMaskPathToContext(ctx, processedPath);
        ctx.fill();
        break;
    }

    ctx.restore();
  }

  // Apply feather (blur)
  if (mask.featherPx > 0) {
    // Use CSS filter for blur (works on OffscreenCanvas)
    const tempCanvas = createCanvas(width, height);
    const tempCtx = tempCanvas.getContext('2d');
    if (tempCtx) {
      tempCtx.filter = `blur(${mask.featherPx}px)`;
      tempCtx.drawImage(canvas, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(tempCanvas, 0, 0);
    }
  }

  // Invert if needed
  if (mask.invert) {
    ctx.globalCompositeOperation = 'difference';
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'source-over';
  }
}

/**
 * Combine mask and gradient alpha canvases
 * Result alpha = 1 - mask + mask * gradient
 * (gradient applies only inside the mask; outside stays fully visible)
 */
export function combineAlphaCanvases(
  output: AnyCanvas,
  maskCanvas: AnyCanvas,
  gradientCanvas: AnyCanvas,
  width: number,
  height: number,
  useMask: boolean
): void {
  const ctx = output.getContext('2d');
  if (!ctx) return;

  if (output.width !== width || output.height !== height) {
    output.width = width;
    output.height = height;
  }

  const gradientCtx = gradientCanvas.getContext('2d');
  if (!gradientCtx) return;

  const gradientData = gradientCtx.getImageData(0, 0, width, height).data;
  const outputImage = ctx.createImageData(width, height);
  const outData = outputImage.data;

  if (!useMask) {
    // No mask: use gradient alpha across full image
    for (let i = 0; i < gradientData.length; i += 4) {
      const grad = gradientData[i];
      outData[i] = 255;
      outData[i + 1] = 255;
      outData[i + 2] = 255;
      outData[i + 3] = grad;
    }
    ctx.putImageData(outputImage, 0, 0);
    return;
  }

  const maskCtx = maskCanvas.getContext('2d');
  if (!maskCtx) return;
  const maskData = maskCtx.getImageData(0, 0, width, height).data;

  // Apply gradient only inside mask: finalAlpha = 1 - mask + mask * gradient
  for (let i = 0; i < gradientData.length; i += 4) {
    const maskAlpha = maskData[i] / 255;
    const gradAlpha = gradientData[i] / 255;
    const finalAlpha = 1 - maskAlpha + maskAlpha * gradAlpha;
    const alphaByte = Math.max(0, Math.min(255, Math.round(finalAlpha * 255)));

    outData[i] = 255;
    outData[i + 1] = 255;
    outData[i + 2] = 255;
    outData[i + 3] = alphaByte;
  }

  ctx.putImageData(outputImage, 0, 0);
}

/**
 * Apply alpha mask to content using destination-in
 */
export function applyAlphaMaskToContent(
  output: AnyCanvas,
  contentSource: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | OffscreenCanvas | ImageBitmap,
  alphaCanvas: AnyCanvas,
  width: number,
  height: number
): void {
  const ctx = output.getContext('2d');
  if (!ctx) return;

  if (output.width !== width || output.height !== height) {
    output.width = width;
    output.height = height;
  }

  // Clear output
  ctx.clearRect(0, 0, width, height);

  // Draw content
  ctx.drawImage(contentSource, 0, 0, width, height);

  // Apply alpha mask using destination-in
  // Alpha is stored in the mask canvas alpha channel
  ctx.globalCompositeOperation = 'destination-in';
  ctx.drawImage(alphaCanvas, 0, 0);
  ctx.globalCompositeOperation = 'source-over';
}

/**
 * Full gradient fade rendering pipeline
 * Returns an ImageBitmap that can be used as Konva.Image source
 */
export async function renderGradientFadeEffect(
  elementId: string,
  contentSource: HTMLImageElement | HTMLVideoElement | HTMLCanvasElement | OffscreenCanvas | ImageBitmap,
  opacitySettings: OpacitySettings,
  width: number,
  height: number,
  forceRefresh: boolean = false
): Promise<ImageBitmap | null> {
  const mask = opacitySettings.mask;
  const gradient = opacitySettings.gradient;
  const useMask = !!mask && mask.enabled && mask.paths.length > 0;

  // Check cache
  const maskHash = hashMask(mask);
  const gradientHash = hashGradient(gradient);
  const cached = renderCache.get(elementId);

  if (
    !forceRefresh &&
    cached &&
    cached.maskHash === maskHash &&
    cached.gradientHash === gradientHash &&
    cached.width === width &&
    cached.height === height &&
    cached.outputBitmap
  ) {
    return cached.outputBitmap;
  }

  // Get pooled canvases
  const maskCanvas = getPooledCanvas('mask', width, height);
  const gradientCanvas = getPooledCanvas('gradient', width, height);
  const alphaCanvas = getPooledCanvas('alpha', width, height);
  const outputCanvas = getPooledCanvas('output', width, height);

  // Render mask (if enabled)
  if (useMask) {
    renderMaskToCanvas(maskCanvas, mask, width, height);
  } else {
    // No mask = full white (apply gradient to full area)
    const ctx = maskCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
    }
  }

  // Render gradient
  if (gradient) {
    renderGradientToCanvas(gradientCanvas, gradient, width, height);
  } else {
    // No gradient = full white
    const ctx = gradientCanvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
    }
  }

  // Combine mask and gradient into final alpha
  combineAlphaCanvases(alphaCanvas, maskCanvas, gradientCanvas, width, height, useMask);

  // Apply to content
  applyAlphaMaskToContent(outputCanvas, contentSource, alphaCanvas, width, height);

  // Create ImageBitmap for Konva
  const outputBitmap = await createImageBitmap(outputCanvas);

  // Update cache
  renderCache.set(elementId, {
    maskHash,
    gradientHash,
    width,
    height,
    outputBitmap,
  });

  return outputBitmap;
}

/**
 * Clear cache for an element (call when element is deleted)
 */
export function clearGradientFadeCache(elementId: string): void {
  const cached = renderCache.get(elementId);
  if (cached?.outputBitmap) cached.outputBitmap.close();
  renderCache.delete(elementId);
}

/**
 * Clear all caches
 */
export function clearAllGradientFadeCaches(): void {
  for (const [, cached] of renderCache) {
    if (cached.outputBitmap) cached.outputBitmap.close();
  }
  renderCache.clear();
}
